import React, { useMemo, useState } from 'react'
import { Chip, IconButton, Tooltip, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { startCase, toLower } from 'lodash'
import ReactMarkdown from 'react-markdown'
import Condition from 'components/Condition'
import ConfirmButton from 'components/ConfirmButton'
import InfoMessage from 'components/InfoMessage'
import { Create, Delete, FiberManualRecord, School, Work } from '@mui/icons-material'
import { useIsPrerendering } from 'hooks/utility'
import { useFragment, useLazyLoadQuery } from 'react-relay/hooks'
import { graphql } from 'react-relay'
import { classList } from 'services/styles'
import { TimelineEntriesField_Query } from '__generated__/TimelineEntriesField_Query.graphql'
import { TimelineEntriesField_User$key } from '__generated__/TimelineEntriesField_User.graphql'
import { useNodes } from 'hooks/relay'
import { TimelineEntriesField_TimelineEntries$key } from '__generated__/TimelineEntriesField_TimelineEntries.graphql'
import { useQuickCommit } from 'hooks/useQuickCommit'
import { TimelineEntriesField_DeleteMutation } from '__generated__/TimelineEntriesField_DeleteMutation.graphql'
import Suspense from 'components/Suspense'
import { fromZodError } from 'zod-validation-error'
import { ZodError } from 'zod'
import moment from 'moment'
import EditTimelineEntryDialog, { formSchema as entryFormSchema } from './components/EditTimelineEntryDialog'
import TabPlaceholder from '../../TabPlaceholder'
import useStyles from './styles'

interface Errors {
  error: string | null
  timelineEntries: {
    [id: string]: string
  }
}

export interface ITimelineEntriesFieldProps {
  editable: boolean
  userId: string
}

function TimelineEntriesField(props: ITimelineEntriesFieldProps) {
  const { classes } = useStyles()
  const theme = useTheme()
  const { editable, userId } = props

  const data = useLazyLoadQuery<TimelineEntriesField_Query>(graphql`
    query TimelineEntriesField_Query($userId: ID!) {
      user: node(id: $userId) {
        ...TimelineEntriesField_User
      }
    }
  `, { userId }, {
    fetchPolicy: 'store-and-network',
  })

  const user = useFragment(graphql`
    fragment TimelineEntriesField_User on User {
      profile {
        industry {
          id
        }
      }

      timelineEntries {
        ...TimelineEntriesField_TimelineEntries
      }
    }
  `, data?.user as TimelineEntriesField_User$key)

  const timelineEntries = useNodes(useFragment(graphql`
    fragment TimelineEntriesField_TimelineEntries on TimelineEntry @relay(plural: true) {
      id
      entryType
      place
      title
      dateStart
      dateEnd
      description

      institute {
        worldRankRange
        rankingYear
      }

      company {
        custom
      }

      skills {
        id
        name
      }
    }
  `, user?.timelineEntries as TimelineEntriesField_TimelineEntries$key))
  const [currentItemId, setCurrentItemId] = useState<string | 'newWork' | 'newEducation' | null>(null)

  const errors = useMemo<Errors>(() => {
    let mainError: string | null = null
    const timelineEntriesErrors: Record<string, string> = {}

    if (editable) {
      if (timelineEntries.filter(entry => entry.entryType === 'work').length === 0 || timelineEntries.filter(entry => entry.entryType === 'education').length === 0) {
        mainError = 'You must have at least one work experience and one education entry'
      }

      timelineEntries.forEach((entry) => {
        try {
          entryFormSchema.parse({
            ...entry,
            dateStart: entry.dateStart ? moment(entry.dateStart).format('YYYY') : null,
            dateEnd: entry.dateEnd ? moment(entry.dateEnd).format('YYYY') : null,
          })
        } catch (error) {
          timelineEntriesErrors[entry.id] = fromZodError(error as ZodError).message
        }
      })
    }

    return {
      error: mainError,
      timelineEntries: timelineEntriesErrors,
    }
  }, [timelineEntries, editable])

  const isEmpty = timelineEntries.length === 0
  const placeholderShown = isEmpty && !editable
  const isPrerendering = useIsPrerendering()

  const { execute: executeDelete } = useQuickCommit<TimelineEntriesField_DeleteMutation>(graphql`
    mutation TimelineEntriesField_DeleteMutation($input: DeleteTimelineEntryInput!) {
      deleteTimelineEntry(input: $input) {
        timelineEntry {
          id @deleteRecord
        }
      }
    }
  `)

  const addWorkEntry = () => {
    setCurrentItemId('newWork')
  }

  const addEducationEntry = () => {
    setCurrentItemId('newEducation')
  }

  const handleEditDialogClose = () => {
    setCurrentItemId(null)
  }

  const handleEdit = (id: string) => {
    setCurrentItemId(id)
  }

  const handleDelete = async (id: string) => {
    setCurrentItemId(null)

    await executeDelete({ input: { timelineEntryId: id } })
  }

  return (
    <React.Fragment>
      {editable && (
        <InfoMessage severity="warning">
          For best results with potential clients, do not leave significant gaps in your timeline.
        </InfoMessage>
      )}

      <Condition condition={placeholderShown}>
        <TabPlaceholder
          title="No work & education history yet"
        />
      </Condition>

      <Condition condition={!placeholderShown}>
        <div className={classes.timeline} data-cy="timeline">
          <div className={classes.midLine} />

          <div className={classes.mainIcon}>
            {editable && (
              <div className={classes.item} data-cy="item">
                <div className={classList(classes.itemIcon, 'item-icon no-border')}>
                  <FiberManualRecord />
                </div>

                <div className={`${classes.itemContent} item-content ${classes.newItem}`}>
                  <div>
                    {isEmpty ? 'Add your Education and Work history' : 'Add a new entry'}
                  </div>

                  {errors.error && (
                    <Typography variant="body2" className={classes.error}>
                      {errors.error}
                    </Typography>
                  )}

                  <div className={classes.addContent}>
                    <Tooltip title="Add position">
                      <IconButton onClick={addWorkEntry} data-cy="add-work" style={{ marginRight: theme.spacing(2) }} color="secondary">
                        <Work />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Add education">
                      <IconButton onClick={addEducationEntry} data-cy="add-education" color="secondary">
                        <School />
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
              </div>
            )}

            {timelineEntries.map((item) => {
              const entryError = errors.timelineEntries[item.id]
              const placeText = item.place
              const place = startCase(toLower(placeText || `(Unknown ${item.entryType === 'work' ? 'Company' : 'Location'})`))
              const title = item.title || 'Untitled Entry'

              return (
                <div key={item.id} className={classes.item} data-cy="item">
                  <div className={classList(classes.itemIcon, 'item-icon')}>
                    {item.entryType === 'education' ? <School /> : <Work />}
                  </div>

                  <div className={classList(classes.itemContent, 'item-content')}>
                    <div>
                      <Typography variant="h4">
                        {title}, {place}{' '}

                        <Condition condition={Boolean(item?.institute?.worldRankRange)}>
                          <Chip
                            label={(
                              <span>
                                {item?.institute?.rankingYear || 2019} World Rank {item?.institute?.worldRankRange}
                              </span>
                            )}
                          />
                        </Condition>

                        <Condition condition={Boolean(item?.company?.custom === false)}>
                          <Chip
                            label={(
                              <span>
                                Fortune 500
                              </span>
                            )}
                          />
                        </Condition>
                      </Typography>
                    </div>

                    <div className={classes.itemYears}>
                      <Typography variant="subtitle2">
                        {item.dateStart || '?'} - {item.dateEnd || 'Present'}
                      </Typography>
                    </div>

                    {entryError && (
                      <Typography variant="body2" className={classes.error}>
                        {entryError}
                      </Typography>
                    )}

                    <div className={classes.itemHighlights}>
                      {!isPrerendering && item.description && (
                        <Typography variant="body2">
                          <ReactMarkdown source={item.description} />
                        </Typography>
                      )}

                      {!!item.skills && item.skills.length > 0 && (
                        <div>
                          {item.skills.map(skill => (
                            <Chip key={skill.id} label={skill.name} style={{ marginRight: theme.spacing(1), marginBottom: theme.spacing(1) }} />
                          ))}
                        </div>
                      )}
                    </div>

                    {editable && (
                      <React.Fragment>
                        <Tooltip title="Edit">
                          <IconButton onClick={() => handleEdit(item.id)} style={{ marginRight: theme.spacing(1) }} color="secondary">
                            <Create />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Delete">
                          <ConfirmButton
                            dialogTitle={item.entryType === 'work' ? 'Delete position' : 'Delete education'}
                            onClick={() => handleDelete(item.id)}
                            component={IconButton}
                            color="secondary"
                          >
                            <Delete />
                          </ConfirmButton>
                        </Tooltip>
                      </React.Fragment>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </Condition>

      {currentItemId && (
      <Suspense>
        <EditTimelineEntryDialog
          open
          onClose={handleEditDialogClose}
          timelineEntryId={currentItemId}
          userId={userId}
        />
      </Suspense>
      )}
    </React.Fragment>
  )
}

export default React.memo(TimelineEntriesField)
